/** Tags as chips grouped by category (plan §1.6: up to ~40 tags — chips wrap, grouped,
 *  "+n" beyond the cap). View mode shows the stored set; edit mode shows the stored set
 *  merged with the draft's, each chip saying whether it stays, was added or was removed
 *  — with an icon and in its accessible name, not by tint alone. */
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ReplayIcon from '@mui/icons-material/Replay';
import { alpha, Box, Chip, Typography } from '@mui/material';
import { useState } from 'react';

export const TAG_CHIP_CAP = 12;

export type TagChipState = 'kept' | 'added' | 'removed';

export interface TagChipItem {
  id: number | string;
  label: string;
  group: string;
  state: TagChipState;
  /** Identity line (`#12 · GOLDEN`) for the tooltip. */
  title?: string;
}

const STATE_SUFFIX: Record<TagChipState, string> = { kept: '', added: ' (added)', removed: ' (removed)' };

/** Groups in first-seen order, chips in the given order. */
function grouped(items: TagChipItem[]): Array<{ group: string; items: TagChipItem[] }> {
  const out: Array<{ group: string; items: TagChipItem[] }> = [];
  for (const it of items) {
    let g = out.find((x) => x.group === it.group);
    if (!g) {
      g = { group: it.group, items: [] };
      out.push(g);
    }
    g.items.push(it);
  }
  return out;
}

export function TagChips({
  items,
  cap = TAG_CHIP_CAP,
  label,
  onRemove,
  onRestore,
}: {
  items: TagChipItem[];
  cap?: number;
  /** The field's own label: a lone group of that name is not captioned again (the slot's
   *  label already says "Access tags"). */
  label?: string;
  /** Edit mode: removing a stored or added tag. */
  onRemove?: (id: number | string) => void;
  /** Edit mode: putting a removed stored tag back. */
  onRestore?: (id: number | string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }
  const groups = grouped(items);
  const lone = groups.length === 1 && groups[0]!.group === label;
  const hidden = expanded ? 0 : Math.max(0, items.length - cap);
  let budget = expanded ? Number.POSITIVE_INFINITY : cap;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 0.5, minWidth: 0 }}>
      {groups.map(({ group, items: groupItems }) => {
        const shown = groupItems.slice(0, Math.max(0, budget));
        budget -= shown.length;
        if (shown.length === 0) return null;
        return (
          <Box
            key={group}
            role="group"
            aria-label={group}
            sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}
          >
            {!lone && (
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>
                {group}
              </Typography>
            )}
            {shown.map((it) => (
              <TagChip key={String(it.id)} item={it} onRemove={onRemove} onRestore={onRestore} />
            ))}
          </Box>
        );
      })}
      {(hidden > 0 || expanded) && items.length > cap && (
        <Box>
          <Chip
            size="small"
            variant="outlined"
            clickable
            label={expanded ? 'Show fewer' : `+${hidden} more`}
            onClick={() => setExpanded((e) => !e)}
            aria-expanded={expanded}
          />
        </Box>
      )}
    </Box>
  );
}

function TagChip({
  item,
  onRemove,
  onRestore,
}: {
  item: TagChipItem;
  onRemove?: ((id: number | string) => void) | undefined;
  onRestore?: ((id: number | string) => void) | undefined;
}) {
  const name = `${item.label}${STATE_SUFFIX[item.state]}`;
  const removed = item.state === 'removed';
  const onDelete = removed ? onRestore : onRemove;
  return (
    <Chip
      size="small"
      variant="outlined"
      title={item.title}
      aria-label={name}
      data-tag-state={item.state}
      label={item.label}
      {...(item.state === 'added' ? { icon: <AddIcon /> } : removed ? { icon: <RemoveIcon /> } : {})}
      {...(onDelete
        ? {
            onDelete: () => onDelete(item.id),
            deleteIcon: removed ? <ReplayIcon aria-label={`Restore ${item.label}`} /> : undefined,
          }
        : {})}
      sx={[
        { maxWidth: '100%' },
        item.state === 'added' && {
          bgcolor: (t) => alpha(t.palette.warning.main, 0.12),
          borderColor: (t) => alpha(t.palette.warning.main, 0.5),
        },
        removed && {
          borderStyle: 'dashed',
          color: 'text.secondary',
          '& .MuiChip-label': { textDecoration: 'line-through' },
        },
      ]}
    />
  );
}
